<script setup lang="ts">
/***
 * template to show array of enums
 */
import { useI18n } from 'vue-i18n';

import { getStatusTagSeverity } from '@/utils/priority';
import { ContractActionV1Dto } from '@/openapi/models/ContractActionV1Dto';

import type { DataTableCellProps } from './templateTypes';

const { t } = useI18n();
const _props = defineProps<DataTableCellProps>();

enum RemainingTimeCategory {
  LESS_THAN_MONTH = 'lessThanMonth',
  LESS_THAN_QUARTER = 'lessThanQuarter',
  LESS_THAN_HALF_YEAR = 'lessThanHalfYear',
  LESS_THAN_YEAR = 'lessThanYear',
  FAR_FUTURE = 'farFuture',
}

function getRemainingTimeCategory(start: string): RemainingTimeCategory | undefined {
  const startDate = new Date(start);
  const today = new Date();

  if (isNaN(startDate.getTime())) {
    return undefined;
  }

  // Zeitanteil ignorieren
  startDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffMs = startDate.getTime() - today.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays <= 30) {
    return RemainingTimeCategory.LESS_THAN_MONTH;
  }
  if (diffDays <= 90) {
    return RemainingTimeCategory.LESS_THAN_QUARTER;
  }
  if (diffDays <= 180) {
    return RemainingTimeCategory.LESS_THAN_HALF_YEAR;
  }
  if (diffDays <= 365) {
    return RemainingTimeCategory.LESS_THAN_YEAR;
  }

  return RemainingTimeCategory.FAR_FUTURE;
}

/**
 * Ermittelt den Status der ContractAction auf Basis von Start- und Enddatum im Verhältnis zu heute.
 */
function getDateStatus(start: string, end: string): ContractActionV1Dto.status | undefined {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const today = new Date();

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return undefined;
  }

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  if (today >= startDate && today <= endDate) {
    return ContractActionV1Dto.status.AVAILABLE;
  }

  if (today > endDate) {
    return ContractActionV1Dto.status.COMPLETED;
  }

  return ContractActionV1Dto.status.ACTIVE;
}
</script>

<template>
  <div class="flex gap-2">
    <div class="flex-1 flex items-center gap-2">
      <Tag
        v-if="
          getDateStatus(
            row.actionStart.meta('rawvalue'),
            row.supposedActionEnd.meta('rawvalue'),
          ) !== 'Active'
        "
        :value="
          t(
            `enums.${getDateStatus(row.actionStart.meta('rawvalue'), row.supposedActionEnd.meta('rawvalue'))?.toUpperCase()}`,
          )
        "
        :severity="
          getStatusTagSeverity(
            getDateStatus(row.actionStart.meta('rawvalue'), row.supposedActionEnd.meta('rawvalue')),
          )
        "
      />
      <span v-else>{{
        t(`labels.remaining.${getRemainingTimeCategory(row.actionStart.meta('rawvalue'))}`)
      }}</span>
    </div>
  </div>
</template>

<style scoped lang="css">
@layer app {
  .nameValue {
    display: block;
    white-space: nowrap;
  }
}
</style>
